import { Accordion } from '@/components/atoms/accordion';
import { QUERY_DIMENSIONS } from '@/graphql/queries/dimensions';
import { useLanguage } from '@/hooks/useLanguage';
import { Flex } from '@/styles/globalStyles';
import { useApolloClient } from '@apollo/client';
import { Skeleton, useMediaQuery } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ScoreFilterContainer } from '../styles';
import { ScoreFilter } from '@/components/molecules/score/filter';
import { useTranslation } from '@/hooks/useTranslation';
import { OpportunityAndRisk } from '@/types/opportunityAndRisk';
import { useTheme } from '@mui/material/styles';
import { Add, Remove } from '@mui/icons-material';
import { DimensionTag } from '@/components/molecules/dimensionTag';

interface Dimension {
  id: number;
  name: string;
  bigquery_id?: string;
}

export interface DimensionFilter {
  context: string;
  dimension_id: number;
  field: string;
  operator: string;
  value: string[];
}

export interface DimensionLevelFilter {
  dimension_id: number | null;
  level_opportunity: string[];
  level_risk: string[];
}

const opportunityAndRiskOptions = [
  OpportunityAndRisk.ALL,
  OpportunityAndRisk.HIGH,
  OpportunityAndRisk.MEDIUM_HIGH,
  OpportunityAndRisk.MEDIUM_LOW,
  OpportunityAndRisk.LOW,
];

interface SearchMunicipalityFormDimensionsProps {
  initialFilters?: DimensionLevelFilter[];
  onFiltersChange?: (filters: DimensionLevelFilter[]) => void;
}

export const SearchMunicipalityFormDimensions = ({
  initialFilters = [],
  onFiltersChange,
}: SearchMunicipalityFormDimensionsProps = {}) => {
  const apolloClient = useApolloClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation();

  const [dimensions, setDimensions] = useState<Dimension[]>([]);
  const [dimensionsLoading, setDimensionsLoading] = useState(false);

  const [expandedDimensionId, setExpandedDimensionId] = useState<number | null>(null);

  // Estado para armazenar os níveis de oportunidade por dimensão
  // Inicializar com base em initialFilters
  const [opportunityLevelsByDimension, setOpportunityLevelsByDimension] = useState<
    Record<number, OpportunityAndRisk[]>
  >(() => {
    const initial: Record<number, OpportunityAndRisk[]> = {};

    // Se não há filtros iniciais, retornar vazio para que seja ALL por padrão
    if (initialFilters.length === 0) {
      return initial;
    }

    initialFilters.forEach((filter) => {
      // Filtrar apenas dimensões específicas (não nulas)
      if (
        filter.dimension_id !== null &&
        filter.level_opportunity &&
        filter.level_opportunity.length > 0
      ) {
        initial[filter.dimension_id] = filter.level_opportunity as OpportunityAndRisk[];
      }
    });
    return initial;
  });

  // Estado para armazenar os níveis de risco por dimensão
  // Inicializar com base em initialFilters
  const [riskLevelsByDimension, setRiskLevelsByDimension] = useState<
    Record<number, OpportunityAndRisk[]>
  >(() => {
    const initial: Record<number, OpportunityAndRisk[]> = {};

    // Se não há filtros iniciais, retornar vazio para que seja ALL por padrão
    if (initialFilters.length === 0) {
      return initial;
    }

    initialFilters.forEach((filter) => {
      // Filtrar apenas dimensões específicas (não nulas)
      if (filter.dimension_id !== null && filter.level_risk && filter.level_risk.length > 0) {
        initial[filter.dimension_id] = filter.level_risk as OpportunityAndRisk[];
      }
    });
    return initial;
  });

  const loading = Array.from({ length: 6 }, (_, index) => index);

  // Ref para armazenar os filtros anteriores como string JSON
  const previousFiltersRef = useRef<string>('');

  // Função para converter os níveis em filtros estruturados agrupados por dimension_id
  const buildDimensionFilters = useCallback(() => {
    const dimensionFiltersMap = new Map<number, DimensionLevelFilter>();

    // Processar filtros de oportunidade
    Object.entries(opportunityLevelsByDimension).forEach(([dimensionId, levels]) => {
      // Filtrar apenas valores válidos (não ALL)
      const validLevels = levels.filter((level) => level !== OpportunityAndRisk.ALL);

      const dimId = Number(dimensionId);
      if (!dimensionFiltersMap.has(dimId)) {
        dimensionFiltersMap.set(dimId, {
          dimension_id: dimId,
          level_opportunity: [],
          level_risk: [],
        });
      }

      if (validLevels.length > 0) {
        const filter = dimensionFiltersMap.get(dimId);
        if (filter) {
          filter.level_opportunity = validLevels;
        }
      }
    });

    // Processar filtros de risco
    Object.entries(riskLevelsByDimension).forEach(([dimensionId, levels]) => {
      // Filtrar apenas valores válidos (não ALL)
      const validLevels = levels.filter((level) => level !== OpportunityAndRisk.ALL);

      const dimId = Number(dimensionId);
      if (!dimensionFiltersMap.has(dimId)) {
        dimensionFiltersMap.set(dimId, {
          dimension_id: dimId,
          level_opportunity: [],
          level_risk: [],
        });
      }

      if (validLevels.length > 0) {
        const filter = dimensionFiltersMap.get(dimId);
        if (filter) {
          filter.level_risk = validLevels;
        }
      }
    });

    // Filtrar apenas dimensões que têm pelo menos um filtro (opportunity ou risk)
    const filters = Array.from(dimensionFiltersMap.values()).filter(
      (filter) => filter.level_opportunity.length > 0 || filter.level_risk.length > 0
    );

    return filters;
  }, [opportunityLevelsByDimension, riskLevelsByDimension]);

  // Atualizar filtros quando as seleções mudarem
  useEffect(() => {
    const filters = buildDimensionFilters();
    const currentFiltersString = JSON.stringify(filters);

    // Verificar se os filtros realmente mudaram
    if (currentFiltersString !== previousFiltersRef.current) {
      previousFiltersRef.current = currentFiltersString;

      // Notificar o componente pai sobre as mudanças nos filtros
      if (onFiltersChange) {
        onFiltersChange(filters);
      }
    }
  }, [buildDimensionFilters, onFiltersChange]);

  const handleOpportunityLevelsChange = useCallback(
    (dimensionId: number) => (levels: OpportunityAndRisk[]) => {
      setOpportunityLevelsByDimension((prev) => ({
        ...prev,
        [dimensionId]: levels,
      }));
    },
    []
  );

  const handleRiskLevelsChange = useCallback(
    (dimensionId: number) => (levels: OpportunityAndRisk[]) => {
      setRiskLevelsByDimension((prev) => ({
        ...prev,
        [dimensionId]: levels,
      }));
    },
    []
  );

  // Resetar os estados quando initialFilters for vazio (quando limpar filtros)
  useEffect(() => {
    if (initialFilters.length === 0) {
      setOpportunityLevelsByDimension({});
      setRiskLevelsByDimension({});
    }
  }, [initialFilters.length]);

  useEffect(() => {
    const getDimensions = async () => {
      try {
        setDimensionsLoading(true);
        const { data } = await apolloClient.query<{
          dimensions: { data: Dimension[] };
        }>({
          query: QUERY_DIMENSIONS,
          variables: {
            pagination: { limit: 10, page: 1 },
            language: currentLanguage,
          },
        });
        setDimensions(data.dimensions.data);
      } catch (error) {
        console.error('Error fetching dimensions:', error);
      } finally {
        setDimensionsLoading(false);
      }
    };
    getDimensions();
  }, [currentLanguage]);

  return (
    <>
      {dimensionsLoading ? (
        <Flex direction="column" gap={0.25}>
          {loading.map((_, index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              height={65}
              width="100%"
              sx={{ transform: 'none' }}
            />
          ))}
        </Flex>
      ) : (
        <Flex direction="column" fullWidth>
          {dimensions.map((dimension) => {
            return (
              <Accordion
                key={dimension.id}
                title={dimension.name}
                defaultExpanded={false}
                sx={{
                  width: '100%',
                  borderLeft: 'none',
                  borderRight: 'none',
                  borderTop: 'none',
                  borderRadius: 0,
                }}
                padding={isMobile ? 0 : 2}
                flexDirection="row-reverse"
                expanded={expandedDimensionId === dimension.id}
                onChange={(_, expanded) => setExpandedDimensionId(expanded ? dimension.id : null)}
                expandIcon={
                  expandedDimensionId === dimension.id ? (
                    <Remove color="action" />
                  ) : (
                    <Add color="action" />
                  )
                }
                tag={
                  initialFilters.length > 0 ? (
                    <DimensionTag
                      opportunityCount={
                        (opportunityLevelsByDimension[dimension.id] || []).filter(
                          (level) => level !== OpportunityAndRisk.ALL
                        ).length
                      }
                      riskCount={
                        (riskLevelsByDimension[dimension.id] || []).filter(
                          (level) => level !== OpportunityAndRisk.ALL
                        ).length
                      }
                    />
                  ) : undefined
                }
              >
                <ScoreFilterContainer sx={{ padding: theme.spacing(2, 0, 1) }}>
                  <ScoreFilter
                    title={t('label.label.opportunity')}
                    options={opportunityAndRiskOptions}
                    filteredOptions={
                      initialFilters.length > 0
                        ? opportunityLevelsByDimension[dimension.id]
                        : undefined
                    }
                    onSelectedOptions={handleOpportunityLevelsChange(dimension.id)}
                    variant="opportunity"
                    disabled={dimension.bigquery_id === 'seguranca'}
                  />

                  <ScoreFilter
                    title={t('label.label.risk')}
                    options={opportunityAndRiskOptions}
                    filteredOptions={
                      initialFilters.length > 0 ? riskLevelsByDimension[dimension.id] : undefined
                    }
                    onSelectedOptions={handleRiskLevelsChange(dimension.id)}
                    variant="risk"
                    disabled={dimension.bigquery_id === 'economica'}
                  />
                </ScoreFilterContainer>
              </Accordion>
            );
          })}
        </Flex>
      )}
    </>
  );
};
